/**
 * Módulo del tutorial (grupo esencial): Un vuelo por la app (KAN-232).
 *
 * Paneo general por encima de TODA la aplicación: las categorías del menú
 * (no cada ítem), los controles del header (buscador ⌘K, modos de color,
 * indicador de en línea, campana de alertas, birrete de tutoriales), el
 * panel de entrada (acciones rápidas, cards personalizables), el foro, la
 * atención al cliente dentro del chat y el asistente de IA. Aclara que cada
 * sección se ve en detalle en su propio tutorial.
 *
 * Anclas: menu-group-* (headers de categoría), header-buscador/tema/online/
 * alertas/tutorial/sync-status, dashboard-quick-panel, dashboard-btn-
 * personalizar, foro-btn-publicar, chat-launcher, chat-handoff, qa-reports.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const paneoAppModule: TutorialModuleDef = {
  id: 'paneo-app',
  title: 'Un vuelo por la app',
  description: 'Un paneo por toda la app: las categorías del menú, el buscador, los avisos, el panel que se personaliza, el foro y el asistente — muy por encima, antes de meternos en cada una.',
  emoji: '🗺️',
  image: 'images/tutorial/paneo-app.png',
  permission: { module: 'animals', action: 'view' },
  grupo: 'esencial',
  route: '/dashboard',
  steps: [
    {
      id: 'concepto-vuelo',
      route: '/tutorial',
      text: 'Ya cargaste tus primeros animales: ahora un VUELO DE PÁJARO por toda la app — muy por encima, para que sepas qué hay y dónde vive cada cosa. El detalle de cada sección viene en su propio tutorial.',
      narration:
        'Antes de seguir metiéndonos de lleno, subamonos un momento al cerro y mirémosla entera, porque ya tenés tus primeros animales adentro y cada rincón te va a sonar. Este es un vuelo de pájaro: tocamos todo muy por encima, sin bajarnos en ninguna parte. El detalle llega después, en el tutorial de cada sección.',
    },
    {
      id: 'categorias-del-menu',
      route: '/dashboard',
      target: '[data-ar-guide="menu-group-ganado"]',
      extraTargets: [
        '[data-ar-guide="menu-group-principal"]',
        '[data-ar-guide="menu-group-campo"]',
        '[data-ar-guide="menu-group-finanzas"]',
        '[data-ar-guide="menu-group-sistema"]',
        '[data-ar-guide="menu-group-acciones"]',
      ],
      label: 'Categorías del menú',
      advanceOnClick: false,
      text: 'El menú del costado está ordenado en CATEGORÍAS (en celular: abrí el ☰): Principal, Ganado, Campo, Finanzas y Sistema. Cada una agrupa lo que vas a usar a diario.',
      narration:
        'Mirá el menú del costado: no es una lista suelta, está ordenado en categorías, como el almacén de pueblo. Principal, con el panel y el mapa; Ganado, con toda la hacienda; Campo, con los campos y ubicaciones; Finanzas, con los números; y Sistema, con las herramientas y configuraciones. Abajo del todo, las Acciones rápidas. Cada categoría tiene su tutorial: por ahora, quedate con el mapa general.',
    },
    {
      id: 'buscador-arriba',
      route: '/dashboard',
      target: '[data-ar-guide="header-buscador"]',
      label: 'Buscar (⌘K)',
      advanceOnClick: false,
      text: 'Arriba a la derecha, el BUSCADOR: escribí lo que quieras — páginas, animales, acciones — y te lleva directo. Se abre con ⌘K (o Ctrl+K). Probalo cuando quieras.',
      narration:
        'Lo primero que te marco arriba, a la derecha: el buscador. Es el atajo más cómodo de la app: escribís lo que sea, una página, una acción, un tutorial, y te lleva derecho. En computadora se abre con la tecla comando K, o control K si sos de Windows. Tocálo, probalo, y seguí.',
    },
    {
      id: 'modos-de-color',
      route: '/dashboard',
      target: '[data-ar-guide="header-tema"]',
      label: 'Modo de color',
      advanceOnClick: false,
      text: 'Al lado, el MODO DE COLOR: claro para la oficina ("Oficina") y oscuro para la noche o el campo ("Oscuro"). Tocálo y probá el que te cansa menos la vista.',
      narration:
        'Al lado del buscador, el modo de color. La app tiene dos caras: la clara, para el día y la oficina, y la oscura, que a la noche o a la intemperie cansa menos la vista. Un toque la cambia, y otro toque la vuelve a cambiar. Asunto de gusto, criollo: probá la tuya.',
    },
    {
      id: 'en-linea-y-sync',
      route: '/dashboard',
      target: '[data-ar-guide="header-online"]',
      extraTargets: ['[data-ar-guide="header-sync-status"]'],
      label: 'En línea / sincronización',
      advanceOnClick: false,
      text: 'El puntito verde dice que estás EN LÍNEA. Al lado, el estado de sincronización: la app funciona igual SIN conexión en el campo, y todo lo que cargás se sincroniza solo cuando vuelve la señal.',
      narration:
        'Este puntito verde te avisa que estás en línea. Y al lado, el estado de sincronización, que es la tranquilidad de esta app: todo lo que cargás se guarda en el teléfono o la compu primero, y cuando hay señal viaja solo a la nube. Te quedaste sin cobertura en el campo? Cargá tranquilo igual: cuando vuelva la señal, se sincroniza todo solo. Si alguna vez queda algo pendiente, ahí te lo dice.',
    },
    {
      id: 'tutoriales-donde',
      route: '/dashboard',
      target: '[data-ar-guide="header-tutorial"]',
      label: '🎓 Tutoriales',
      advanceOnClick: false,
      text: 'El BIRRETE 🎓 te trae a estos tutoriales cuando quieras repetirlos o seguir con nuevos — está siempre arriba, a mano.',
      narration:
        'Y este birrete de la esquina son estos mismos tutoriales: el que estás haciendo ahora y todos los demás. Está siempre arriba, en la misma barra, en cualquier página. Se te olvidó cómo se hacía algo? Birrete, y a repasar. Ahora no lo toques, que estamos volando.',
    },
    {
      id: 'alertas-que-son',
      route: '/dashboard',
      target: '[data-ar-guide="header-alertas"]',
      label: '🔔 Pendientes y avisos',
      advanceOnClick: false,
      text: 'La CAMPANA te avisa cuando algo necesita atención: animales sin carga, preñeces vencidas, listos para vender… Al tocarla vas a los "Pendientes y Avisos" del panel. Ámbar fuerte = urgente; ámbar suave = para revisar.',
      narration:
        'La campana es el capataz de la app: te avisa cuando algo necesita ojos. Animales que quedaron sin cargar, vaquillonas con la preñez vencida, terneros listos para vender. La tocás y te lleva a los pendientes y avisos del panel de entrada, cada uno con su botón para ir derecho al asunto. Los avisos fuerte, en ámbar encendido, son los urgentes; los suavecitos, para revisar con calma. No es ruido, es el campo hablándote.',
    },
    {
      id: 'panel-principal',
      route: '/dashboard',
      target: '[data-ar-guide="dashboard-quick-panel"]',
      label: 'Acciones Rápidas del panel',
      advanceOnClick: false,
      text: 'En el Panel de Entrada viven las ACCIONES RÁPIDAS, siempre a la vista: los botones que más se usan (carga rápida, nueva sesión, cargar gasto, registrar lluvia…).',
      narration:
        'Bajemos al panel de entrada, la página que te recibe. Arriba de todo, las acciones rápidas: un puñado de botones para lo que se hace todos los días, la carga rápida para llenar el campo de una, arrancar una sesión, cargar un gasto, anotar una lluvia. Siempre están desplegadas, a la vista. Ese "Resumen de tu campo" que estás viendo es tu tablero.',
    },
    {
      id: 'acciones-del-menu',
      route: '/dashboard',
      target: '[data-ar-guide="menu-group-acciones"]',
      extraTargets: [
        '[data-ar-guide="menu-quick-gasto"]',
        '[data-ar-guide="qa-animals-new"]',
      ],
      label: 'Acciones del menú',
      advanceOnClick: false,
      text: 'Esas mismas acciones viven también en el MENÚ del costado, siempre a mano y en el mismo orden: Carga Rápida, Nueva Sesión, Nueva Venta, Nueva Compra, y cargar Ingreso, Gasto o Consumo directo.',
      narration:
        '¿Y si no querés pasar por el panel? Las mismas acciones viven en el menú del costado, abajo del todo, siempre a la vista y en el mismo orden que en el panel: la carga rápida, la nueva sesión, la venta y la compra, y los atajos para cargar un ingreso, un gasto o consumir del inventario sin vueltas. Del lado que más te guste: llegan al mismo lugar.',
    },
    {
      id: 'cards-de-info',
      route: '/dashboard',
      target: '[data-ar-guide="dashboard-cards"]',
      label: 'Las tarjetas de tu campo',
      advanceOnClick: false,
      text: 'Estas TARJETAS de información resumen tu campo: cabezas activas, tasa de preñez, peso promedio de los terneros… Y cada una te lleva al reporte completo con un toque.',
      narration:
        'Bajemos a esta zona que te marco: las tarjetas de información, el resumen del campo en un vistazo. Cuántas cabezas activas, cuántas vacas, la tasa de preñez, el peso promedio de los terneros, los nacimientos. Y no son adorno: tocás cualquiera y te lleva al reporte completo de ese número, para verlo con detalle.',
    },
    {
      id: 'personalizar-panel',
      route: '/dashboard',
      target: '[data-ar-guide="dashboard-btn-personalizar"]',
      label: 'Personalizar panel',
      advanceOnClick: false,
      text: 'Tocá "Personalizar" y elegí qué tarjetas ver: hay de PRODUCCIÓN, REPRODUCCIÓN, PESOS, PÉRDIDAS, NACIMIENTOS… Probá marcar alguna de prueba. La app recuerda tu selección y mantiene tu panel a tu gusto EN ESTA máquina.',
      narration:
        'Y lo mejor del panel: es tuyo. Tocá personalizar y elegís qué tarjetas ver y cuáles no: hay de producción, de reproducción, de pesos, de pérdidas, de nacimientos. Marcá una de prueba y mirá: la app se acuerda de tu elección y te mantiene el panel con los números que a vos te importan, en esta máquina. Cada compu o celular tiene su panel, como cada uno tiene su escritorio. Cuando cierres el panel, seguimos.',
    },
    {
      id: 'ir-a-inicio',
      route: '/dashboard',
      target: '[data-ar-guide="sidebar-inicio-publico"]',
      label: '🏠 Inicio (abajo del menú)',
      text: 'El foro vive en la PÁGINA DE ENTRADA (la pública): tocá "Página de inicio" — la casita abajo del todo del menú — y te llevo.',
      narration:
        'Ahora te quiero mostrar el foro de la comunidad, pero vive en la página de entrada, la pública, la misma que ve la gente cuando entra al sitio. Para llegar: bajá al final del menú del costado y tocá Página de inicio, la casita, justo arriba de Cerrar sesión. De ahí seguimos juntos.',
    },
    {
      id: 'foro-entrada',
      route: '/',
      target: '[data-ar-guide="nav-foro"]',
      label: 'Foro (barra de arriba)',
      text: 'En la barra de arriba de la página de entrada está el FORO — tocá "Foro" para entrar.',
      narration:
        '¿Ves la barra de arriba? Ahí está el Foro, junto a Precios y Funcionalidades. Tocalo y entramos a la comunidad.',
    },
    {
      id: 'foro-publicar',
      route: '/foro',
      target: '[data-ar-guide="foro-btn-publicar"]',
      label: '✏️ Publicar un tema',
      advanceOnClick: false,
      text: '¿Dudas o para compartir? En el FORO podés publicar un tema: título, categoría y tu consulta — otros productores y el equipo te responden.',
      narration:
        'Ya estamos en el foro de la comunidad. Acá se pregunta y se comparte entre productores: dudas de cría, de armas, de la app misma. Para publicar tu tema, tocás acá, le ponés título, categoría y contás tu consulta. Leelo primero, que hay buena experiencia acumulada de gente del palo.',
    },
    {
      id: 'asistente-general',
      route: '/dashboard',
      target: '[data-ar-guide="chat-launcher"]',
      label: '🐄 Asistente',
      text: 'Y el ASISTENTE de IA: la vaquita de la esquina inferior derecha — tocála. Preguntale lo que quieras de tu campo o cómo usar la app, hasta por voz.',
      narration:
        'Volvemos al panel, y acá está el compañero de todos los días: el asistente. La vaquita que vive en la esquina inferior derecha. Tocala y abrila: le preguntás cuánto ganaron los terneros, cómo se declara en SENASA, cómo se conecta la balanza, lo que sea. Entiende el castellano de todos los días, contesta con los datos de TU campo, y hasta le podés hablar por voz. Tocala para abrirlo.',
    },
    {
      id: 'atencion-al-cliente',
      route: '/dashboard',
      target: '[data-ar-guide="chat-handoff"]',
      label: '🎧 Atención al cliente',
      advanceOnClick: false,
      text: 'Dentro del chat, este botón te conecta con "Atención al cliente": una PERSONA del equipo. Tu pedido queda en la cola — tardan un tiempo en responder, con paciencia se llega. Cerrá el chat con la ✕ para seguir.',
      narration:
        'Con el chat abierto, mirá arriba: este auricular es la atención al cliente, personas de carne y hueso del equipo. Cuando el asistente no alcanza o preferís hablar con alguien, lo tocás y quedás en la cola de atención. Un consejo de paisano: no esperes respuesta en el acto, tardan su tiempo, pero el pedido queda registrado y te contestan. Listo el chat? Cerralo con la cruz y volvemos.',
    },
    {
      id: 'reportes-donde',
      route: '/dashboard',
      target: '[data-ar-guide="qa-reports"]',
      extraTargets: ['[data-ar-guide="menu-group-ganado"]'],
      label: 'Reportes (en Ganado)',
      advanceOnClick: false,
      text: '¿Y los REPORTES? Viven adentro de la categoría Ganado: "Reporte del Campo" — preñez, sanidad, productividad, distribución… Toda la radiografía de tu campo en un solo lugar.',
      narration:
        '¿Y dónde están los reportes, preguntarás? En la categoría Ganado del menú: Reporte del Campo. Ahí entran los análisis de preñez, de sanidad, de productividad, la distribución de la hacienda, la evolución del peso. Toda la radiografía del campo, en un solo toque. Cada uno se ve a fondo en su propio tutorial.',
    },
    {
      id: 'cierre-vuelo',
      route: '/dashboard',
      text: 'Eso fue el mapa general — cada cosa que viste de pasada tiene su tutorial dedicado. Volvé al birrete 🎓 y seguí con el que más te sirva.',
      narration:
        'Y con esto cerramos el vuelo, criollo. Viste el menú entero, el buscador, los modos de color, la campana de avisos, el panel que se personaliza, el foro, la atención al cliente y el asistente. Todo eso que viste de pasada tiene su propio tutorial, con práctica y detalle, como el que hiciste con tus animales. Cuando quieras, birrete al frente, y a seguir aprendiendo. La app ya es tuya.',
    },
  ],
  completionCheck: async () => false, // se completa haciendo el recorrido
};
